import { P2GBox, P2GGrid, P2GText } from '@parcel2go/core-ui';
import { makeStyles } from 'tss-react/mui';
import { toDisplayCurrency } from '~/lib/prices';
import { Currency } from '~/lib/types/basket-types';

export type OrderBreakdownItem = {
  name: string;
  price: number;
  highlight?: boolean;
};

export type OrderBreakdownProps = {
  items: OrderBreakdownItem[];
  title?: string;
  currency: Currency;
  total?: OrderBreakdownItem;
};

const useStyles = makeStyles()((theme) => ({
  root: {
    p: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2)
    }
  },
  breakdownTitle: {
    marginBottom: theme.spacing(2)
  },
  breakdownItem: {
    marginBottom: theme.spacing(1)
  },
  orderTotal: {
    backgroundColor: theme.palette.grey.A100,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.grey[200]}`,
    marginTop: theme.spacing(3)
  }
}));

export const OrderBreakdown = ({ title, items, currency, total }: OrderBreakdownProps) => {
  const { classes } = useStyles();

  return (
    <P2GBox className={classes.root} dataTestId="sidebar-breakdown">
      {title && (
        <P2GText
          component="p"
          variant="subtitle2"
          className={classes.breakdownTitle}
          dataTestId="order-breakdown-title"
        >
          {title}
        </P2GText>
      )}

      {items.map((item) => {
        const fontWeight = item.highlight ? 'bold' : undefined;
        const padding = item.highlight ? 1 : 0;
        return (
          <P2GGrid
            container
            alignItems="center"
            justifyContent="space-between"
            key={item.name}
            className={classes.breakdownItem}
            dataTestId={`order-breakdown-${item.name.replace(/\s+/g, '-').toLowerCase()}`}
            pt={padding}
            pb={padding}
          >
            <P2GText component="p" variant="body2" flexGrow={1} fontWeight={fontWeight}>
              {item.name}
            </P2GText>
            {item.price === 0 ? (
              <P2GText component="p" variant="body2" fontWeight={fontWeight}>
                Free
              </P2GText>
            ) : (
              <P2GText component="p" variant="body2" dataTestId="order-breakdown-total-price" fontWeight={fontWeight}>
                {toDisplayCurrency(currency, item.price)}
              </P2GText>
            )}
          </P2GGrid>
        );
      })}

      {total && (
        <P2GGrid container className={classes.orderTotal} dataTestId="order-breakdown-total">
          <P2GText
            component="p"
            variant="subtitle2"
            color="textSecondary"
            dataTestId="order-breakdown-total-title"
            flexGrow={1}
          >
            {total.name}
          </P2GText>
          {total.price === 0 ? (
            <P2GText component="p" variant="body2" dataTestId="order-breakdown-total-price">
              Free
            </P2GText>
          ) : (
            <P2GText component="p" variant="subtitle2" dataTestId="order-breakdown-total-price" align="left">
              {toDisplayCurrency(currency, total.price)}
            </P2GText>
          )}
        </P2GGrid>
      )}
    </P2GBox>
  );
};
